import { Stack, styled, Tab, tabClasses, TabList, Tabs, Typography } from '@healthinal/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AnamnesisDtoAnamnesisType } from '../../../api/generated/model';
import { useGetAnamnesesByPatientSuspense } from '../../../api/generated/patient-controller/patient-controller.ts';
import { getAnamnesisTypeName } from '../../../utils/anamnesis.ts';
import { AnamnesisInstitutionCard } from './components/anamnesis/AnamnesisInstitutionCard.tsx';
import { ClinicalReportAnamnesisCard } from './components/anamnesis/ClinicalReportAnamnesisCard.tsx';
import { ImagingDiagnosisCard } from './components/anamnesis/ImagingDiagnosisCard.tsx';
import { PatientCategoryCard } from './components/anamnesis/PatientCategoryCard.tsx';
import { SecondaryDiagnosesCard } from './components/anamnesis/SecondaryDiagnosesCard.tsx';
import { SocialAnamnesisCard } from './components/anamnesis/SocialAnamnesisCard.tsx';
import { SymptomsCard } from './components/anamnesis/SymptomsCard.tsx';

interface SearchParams {
  selectedAnamnesisType: string;
}
export const Route = createFileRoute('/patients/$patientId/anamnesis')({
  component: Anamnesis,
  staticData: { title: 'Anamnese' },
  validateSearch: (search): SearchParams => {
    return {
      selectedAnamnesisType: (search?.selectedAnamnesisType as string) ?? AnamnesisDtoAnamnesisType.MEDICAL,
    };
  },
});

function Anamnesis() {
  const { patientId } = Route.useParams();
  const { selectedAnamnesisType } = Route.useSearch();
  const { data: anamneses } = useGetAnamnesesByPatientSuspense(patientId);

  const navigate = useNavigate();

  return (
    <>
      <Stack direction="row" alignItems="start" justifyContent="space-between">
        <Tabs
          value={selectedAnamnesisType}
          onChange={(_, value) =>
            typeof value === 'string' && navigate({ replace: true, to: '.', search: { selectedAnamnesisType: value } })
          }>
          <StylesTabList disableUnderline>
            {Object.values(AnamnesisDtoAnamnesisType).map((type) => (
              <Tab key={type} value={type}>
                {getAnamnesisTypeName(type)}
              </Tab>
            ))}
          </StylesTabList>
        </Tabs>
      </Stack>
      <Stack gap={2}>
        <Typography level={'h3'}>{getAnamnesisTypeName(anamneses[selectedAnamnesisType].anamnesisType)}</Typography>
        <Stack direction="row" gap={2}>
          <Stack gap={2} flex={1}>
            {selectedAnamnesisType === AnamnesisDtoAnamnesisType.MEDICAL && (
              <AnamnesisInstitutionCard
                anamnesisId={anamneses[selectedAnamnesisType].id!}
                patientId={patientId}
                social={anamneses[selectedAnamnesisType].social}
              />
            )}
            <SymptomsCard
              anamnesisId={anamneses[selectedAnamnesisType].id!}
              symptoms={anamneses[selectedAnamnesisType].symptoms}
              symptomsNote={anamneses[selectedAnamnesisType].symptomsNote}
              patientId={patientId}
            />
            <SecondaryDiagnosesCard
              anamnesisId={anamneses[selectedAnamnesisType].id!}
              patientId={patientId}
              diagnoses={anamneses[selectedAnamnesisType].secondaryDiagnoses}
              secondaryDiagnosesNote={anamneses[selectedAnamnesisType].secondaryDiagnosesNote}
            />
            {selectedAnamnesisType === AnamnesisDtoAnamnesisType.MEDICAL && (
              <ImagingDiagnosisCard
                diagnoses={anamneses[selectedAnamnesisType].imagingDiagnoses}
                anamnesisId={anamneses[selectedAnamnesisType].id!}
                patientId={patientId}
              />
            )}
          </Stack>
          <Stack gap={2} flex={1}>
            <PatientCategoryCard
              anamnesisId={anamneses[selectedAnamnesisType].id!}
              patient={anamneses[selectedAnamnesisType].patientCategory}
              patientId={patientId}
              selectedAnamnesisType={selectedAnamnesisType}
            />
            <SocialAnamnesisCard
              anamnesisId={anamneses[selectedAnamnesisType].id!}
              patientId={patientId}
              social={anamneses[selectedAnamnesisType].social}
              selectedAnamnesisType={selectedAnamnesisType}
            />
            {selectedAnamnesisType === AnamnesisDtoAnamnesisType.MEDICAL &&
              anamneses[selectedAnamnesisType].clinicalReportAnamnesis && (
                <ClinicalReportAnamnesisCard anamnesis={anamneses[selectedAnamnesisType].clinicalReportAnamnesis} />
              )}
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}

const StylesTabList = styled(TabList)(({ theme }) => ({
  backgroundColor: theme.vars.palette.background.body,
  [`& .${tabClasses.root}`]: {
    [`&.${tabClasses.selected}`]: {
      backgroundColor: theme.vars.palette.background.body,
      color: theme.vars.palette.primary.plainColor,
    },
  },
}));
